import {
  Box,
  Card,
  IconButton,
  Stack,
  styled,
  Typography,
  useTheme,
} from '@mui/material';
import Delete from '@mui/icons-material/Delete';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import ReadMoreText from '@ui-framework/components/UI/ReadMoreText';
import {
  getFileExtension,
  SnackbarApiErrorMessage,
  useDataProvider,
  useDialog,
  useLocalization,
  useMasterData,
  useSnackbar,
} from '@ui-framework/index';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { toLongDate } from '@utils/Dated';
import { capitalCase } from 'case-anything';
import React, { useEffect, useState } from 'react';
import FileIcon from './FileIcon';
import ModalViewer from '../../Viewers/ModalViewer';
import { getDownloadUrl, deleteAttachment } from './lib/lib';
import { isSupportedByModalViewer } from '../../Viewers';

const FileCard = styled(Card)({
  borderRadius: '15px',
  padding: '10px',
});

function FileDetail({
  file,
  noteId,
  serviceJobId,
  reloadAttachmentList = () => undefined,
}) {
  const theme = useTheme();
  const masterData = useMasterData();
  const snackbar = useSnackbar();
  const { formatMessage } = useLocalization();
  const { confirmationRef } = useDialog();
  const [showFileViewer, setShowFileViewer] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState();

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { get, getApiUrl, remove } = useDataProvider(provider);

  const commonParams = {
    get,
    remove,
    getApiUrl,
  };

  const getAttachmentTypeLabel = value =>
    masterData?.attachmentTypes?.find(item => item.value === value)?.label ??
    '';

  const getModalTitleForFile = fileObj => {
    let title = capitalCase(fileObj?.attachmentType, {
      keepSpecialCharacters: false,
    });
    if (fileObj?.description) {
      title = `${title} : ${fileObj.description}`;
    }
    return title;
  };

  const deleteFile = () => {
    confirmationRef.current.init({
      message: formatMessage({
        id: 'DO_YOU_WANT_TO_DELETE_THIS_ATTACHMENT',
        defaultMessage: 'Do you want to delete this attachment?',
      }),
      positiveButtonText: formatMessage({
        id: 'YES',
        defaultMessage: 'Yes',
      }),
      negativeButtonText: formatMessage({
        id: 'CANCEL',
        defaultMessage: 'Cancel',
      }),
      loadingMessage: formatMessage({
        id: 'DELETING_THE_ATTACHMENT',
        defaultMessage: 'Deleting the attachment...',
      }),
      title: formatMessage({
        id: 'DELETE_ATTACHMENT',
        defaultMessage: 'Delete Attachment',
      }),
      onChange: async value => {
        if (value) {
          confirmationRef.current.setLoading(true);
          try {
            const deleteAttachmentResponse = await deleteAttachment({
              params: commonParams,
              serviceJobId,
              noteId,
              fileId: file?.id,
            });
            if (deleteAttachmentResponse?.ok) {
              snackbar.showMessage(
                formatMessage({
                  id: 'ATTACHMENT_WAS_DELETED_SUCCESSFULLY',
                  defaultMessage: 'Attachment was deleted successfully',
                }),
              );
              reloadAttachmentList();
            } else {
              snackbar.showMessage({
                type: 'error',
                data: (
                  <SnackbarApiErrorMessage
                    errors={deleteAttachmentResponse?.errors}
                  />
                ),
              });
            }
          } catch (err) {
            snackbar.showMessage({
              type: 'error',
              data: (
                <SnackbarApiErrorMessage errors={[{ message: err?.message }]} />
              ),
            });
          }
          confirmationRef.current.setLoading(false);
        }
        confirmationRef.current.setOpen(false);
      },
    });
    confirmationRef.current.setOpen(true);
  };

  const initialize = async () => {
    try {
      const downloadResponse = await getDownloadUrl({
        params: commonParams,
        serviceJobId,
        noteId,
        attachmentid: file?.id,
      });
      if (downloadResponse?.ok) {
        setDownloadUrl(downloadResponse?.data?.presignedUrl);
      }
    } catch {
      // TODO: figure out waht to do here
    }
  };

  useEffect(() => {
    initialize();
  }, [noteId]);

  return (
    <Box sx={{ width: '100%', my: 0.5 }}>
      <FileCard variant="outlined">
        <Grid2 container alignItems="center">
          <Grid2>
            <FileIcon color="primary" ext={getFileExtension(file.fileName)} />
          </Grid2>
          <Grid2 xs={4}>
            <Typography
              color="primary"
              variant="body1"
              sx={{ lineHeight: '1.1em', fontWeight: 500 }}>
              {getAttachmentTypeLabel(file?.attachmentType)}{' '}
              {file?.description ? `(${file?.description})` : ''}
            </Typography>
            <ReadMoreText maxChars={50}>{file.fileName}</ReadMoreText>
          </Grid2>
          <Grid2 xs={2}>
            <Typography variant="body1" sx={{ lineHeight: '1em' }}>
              {file?.uploadedBy?.firstName} {file?.uploadedBy?.lastName}
            </Typography>
          </Grid2>
          <Grid2 xs={2}>
            <Typography variant="body2">
              {toLongDate(file?.uploadedDateTime)}
            </Typography>
          </Grid2>
          <Grid2 xs={2}>
            <Stack gap={2} direction="row" justifyContent="right">
              {!isSupportedByModalViewer(file?.fileName) ? (
                <IconButton
                  sx={{ background: theme.palette.secondary.light }}
                  target="_blank"
                  rel="noreferrer"
                  href={downloadUrl}>
                  <CloudDownloadIcon color="primary" />
                </IconButton>
              ) : (
                <Box>
                  <IconButton
                    onClick={() => setShowFileViewer(true)}
                    sx={{ background: theme.palette.secondary.light, mr: 1 }}>
                    <RemoveRedEyeIcon color="primary" />
                  </IconButton>
                  <IconButton
                    sx={{ background: theme.palette.secondary.light }}
                    target="_blank"
                    rel="noreferrer"
                    href={downloadUrl}>
                    <CloudDownloadIcon color="primary" />
                  </IconButton>
                </Box>
              )}
              {file.canUpdate && (
                <IconButton
                  onClick={() => {
                    deleteFile();
                  }}
                  sx={{ background: theme.palette.secondary.light }}>
                  <Delete color="error" />
                </IconButton>
              )}
            </Stack>
          </Grid2>
        </Grid2>
      </FileCard>
      <ModalViewer
        open={showFileViewer}
        onClose={() => setShowFileViewer(false)}
        title={getModalTitleForFile(file)}
        fileType={getFileExtension(file?.fileName)}
        fileUrl={downloadUrl}
      />
    </Box>
  );
}

export default FileDetail;
